import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import React from "react";

import { Typography } from "../../components/Typography";
import {
    ProductReviewWrapper,
    ProductReviewRatingWrapper,
    ProductReviewListSettingsWrapper,
    ProductReviewListFilterWrapper,
    ProductReviewListSortWrapper,
    ProductReviewListWrapper,
    ProductReviewPaginationContainer,
    RatingListWrapper,
    RatingItem,
    ReviewRatingAverageWrapper,
    ReviewRatingGrid,
    ReviewRatingGridItem,
    ProgressBarContainer,
    ProgressBarFill,
    ProductReviewRatingSection,
    ProductReviewListItemWrapper,
    ProductReviewListItemInnerWrapper,
    ProductReviewTitleWrapper,
} from "./ProductReviewLayoutComponents";

export function ProductReviewSkeleton() {
    return (
        <>
            <ProductReviewWrapper>
                {/* Rating Section Skeleton */}
                <ProductReviewRatingWrapper>
                    <ProductReviewRatingSection>
                        <ReviewRatingAverageWrapper>
                            <Skeleton variant="text" width={100} height={24} />
                            <Box display="flex" gap={1} alignItems="center">
                                <Skeleton variant="text" width={40} height={40} />
                                <Skeleton variant="text" width={20} height={24} />
                            </Box>
                            <Box display="flex">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <StarSkeleton key={i} />
                                ))}
                            </Box>
                            <Skeleton variant="text" width={120} height={20} />
                        </ReviewRatingAverageWrapper>
                    </ProductReviewRatingSection>
                    <ProductReviewRatingSection data-ref="rating-details">
                        <ReviewRatingGrid>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <React.Fragment key={i}>
                                    <ReviewRatingGridItem>
                                        <Typography variant="body">
                                            <Skeleton variant="text" width={8} height={20} />
                                        </Typography>
                                        <StarSkeleton />
                                    </ReviewRatingGridItem>
                                    <ReviewRatingGridItem>
                                        <ProgressBarContainer>
                                            <ProgressBarFill style={{ width: 0 }} />
                                        </ProgressBarContainer>
                                    </ReviewRatingGridItem>
                                    <ReviewRatingGridItem>
                                        <Skeleton variant="text" width={20} height={20} />
                                    </ReviewRatingGridItem>
                                </React.Fragment>
                            ))}
                        </ReviewRatingGrid>
                    </ProductReviewRatingSection>
                </ProductReviewRatingWrapper>

                {/* List Settings Skeleton */}
                <ProductReviewListSettingsWrapper>
                    <ProductReviewListFilterWrapper>
                        <RatingListWrapper>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <React.Fragment key={index}>
                                    <RatingItem>
                                        <Skeleton variant="text" width={8} height={20} />
                                        <StarSkeleton />
                                    </RatingItem>
                                    {index < 5 - 1 && <Skeleton variant="text" width={1} height={20} />}
                                </React.Fragment>
                            ))}
                        </RatingListWrapper>
                    </ProductReviewListFilterWrapper>
                    <ProductReviewListSortWrapper>
                        <Skeleton variant="rectangular" width={120} height={24} />
                    </ProductReviewListSortWrapper>
                </ProductReviewListSettingsWrapper>

                {/* Review List Skeleton */}
            </ProductReviewWrapper>

            {/* Pagination Skeleton */}
            <ProductReviewPaginationContainer>
                <Box display="flex" gap={1}>
                    <Skeleton variant="rectangular" width={32} height={32} style={{ borderRadius: 16 }} />
                    <Skeleton variant="rectangular" width={32} height={32} style={{ borderRadius: 16 }} />
                </Box>
            </ProductReviewPaginationContainer>
        </>
    );
}

export function ProductReviewListSkeleton({ className }: { className?: string }) {
    return (
        <ProductReviewListWrapper className={className}>
            {Array.from({ length: 3 }).map((_, index) => (
                <ProductReviewListItemWrapper key={index}>
                    <ProductReviewListItemInnerWrapper>
                        <ProductReviewTitleWrapper>
                            <Box display="flex">
                                {Array.from({ length: 5 }).map((_, i) => (
                                    <StarSkeleton key={i} />
                                ))}
                            </Box>
                            <Skeleton variant="rectangular" width="40%" height={16} />
                        </ProductReviewTitleWrapper>
                        <Skeleton variant="text" width="25%" height={16} />
                        <Box display="flex" flexDirection="column">
                            <Skeleton variant="text" width="60%" height={16} />
                            <Skeleton variant="text" width="80%" height={16} />
                            <Skeleton variant="text" width="50%" height={16} />
                        </Box>
                        <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <Skeleton
                                    key={i}
                                    variant="rectangular"
                                    width={100}
                                    height={70}
                                    sx={{ borderRadius: 1 }}
                                />
                            ))}
                        </Box>
                        <Box display="flex" flexDirection="column">
                            <Skeleton variant="text" width="40%" height={16} />
                            <Skeleton variant="text" width="60%" height={16} />
                            <Skeleton variant="text" width="35%" height={16} />
                        </Box>
                    </ProductReviewListItemInnerWrapper>
                </ProductReviewListItemWrapper>
            ))}
        </ProductReviewListWrapper>
    );
}

const StarSkeleton = styled(Skeleton)({
    width: 16,
    height: 16,
    // Remove default border-radius so clipPath works fully
    borderRadius: 0,
    // Rough star shape
    clipPath: "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 90%, 50% 70%, 21% 90%, 32% 57%, 2% 35%, 39% 35%)",
});
